import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Product } from '@skytech/shared';

import { useCart } from './store';
import { useRelated } from '../products/api';
import { OptimizedImage } from '../../components/OptimizedImage';
import { InlineSpinner } from '../../components/ui/Spinner';
import { toast } from '../../components/ui/toast';
import { formatKES } from '../../lib/format';

/**
 * In-cart cross-sell ("Related products") — curated related products for the first cart
 * line, minus what's already in the cart. Uses the same add() so items append in place.
 * Related-product fallback does not imply accessory compatibility.
 */
export function CartCrossSell({
  compact = false,
  disabled = false,
  onAddProduct,
  onNavigate,
}: {
  compact?: boolean;
  disabled?: boolean;
  onAddProduct?: (product: Product) => Promise<void>;
  onNavigate?: () => void;
}): JSX.Element | null {
  const items = useCart((s) => s.items);
  const add = useCart((s) => s.add);
  const first = items[0];
  const { data: related = [] } = useRelated(first?.slug);

  const inCart = new Set(items.map((i) => i.productId));
  const suggestions = related.filter((p) => !inCart.has(p.id)).slice(0, 4);
  if (!first || suggestions.length === 0) return null;

  return (
    <div className={compact ? 'border-t border-frost px-5 py-4' : 'mt-10'}>
      <h2
        className={
          compact ? 'text-sm font-medium text-navy-900' : 'text-xl font-light text-navy-900'
        }
      >
        Related products
      </h2>
      <div className="mt-3 flex gap-3 overflow-x-auto pb-1 [scrollbar-width:thin]">
        {suggestions.map((p) => (
          <MiniCard
            key={p.id}
            product={p}
            disabled={disabled}
            onNavigate={onNavigate}
            onAdd={() => (onAddProduct ? onAddProduct(p) : add(p, 1, { silent: true }))}
          />
        ))}
      </div>
    </div>
  );
}

function MiniCard({
  product,
  onAdd,
  disabled,
  onNavigate,
}: {
  product: Product;
  onAdd: () => Promise<void>;
  disabled: boolean;
  onNavigate?: () => void;
}): JSX.Element {
  const [adding, setAdding] = useState(false);
  const img = product.images[0];
  const out = product.stockQuantity <= 0;

  return (
    <div className="flex w-40 shrink-0 flex-col rounded-xl border border-frost bg-white p-3">
      <Link
        to={`/products/${product.slug}`}
        onClick={onNavigate}
        className="block aspect-square overflow-hidden rounded-md border border-frost bg-cloud"
      >
        {img ? (
          <OptimizedImage
            path={img.path}
            displayOrder={img.displayOrder}
            alt={img.alt ?? product.name}
            width={img.width}
            height={img.height}
            size="thumbnail"
            aspectRatio="1 / 1"
            imageClassName="p-2"
            className="h-full w-full"
          />
        ) : (
          <div className="flex h-full items-center justify-center text-[10px] text-neutral">
            No image
          </div>
        )}
      </Link>
      <Link
        to={`/products/${product.slug}`}
        onClick={onNavigate}
        className="mt-3 min-h-8 line-clamp-2 text-xs leading-snug text-navy-900 hover:text-sky-ink"
      >
        {product.name}
      </Link>
      <span className="mt-auto pt-2 text-xs font-medium tabular-nums text-navy-900">
        {formatKES(product.price)}
      </span>
      <button
        type="button"
        disabled={out || adding || disabled}
        onClick={() => {
          if (adding || disabled) return;
          setAdding(true);
          void onAdd()
            .catch(() => toast.error('This product could not be added. Please try again.'))
            .finally(() => setAdding(false));
        }}
        className="mt-3 inline-flex min-h-11 items-center justify-center gap-1 rounded-md border border-sky-500 py-1 text-[11px] font-medium text-sky-ink hover:bg-sky-500/5 disabled:opacity-50"
      >
        {adding && <InlineSpinner className="h-3 w-3" />}
        {out ? 'Sold out' : 'Add'}
      </button>
    </div>
  );
}
